import React, { useState } from "react";
import api, { fmtErr } from "@/lib/api";
import { ShieldCheck, Smartphone, Mail, KeyRound, Copy, Check } from "lucide-react";

export function MfaStep({ mfa, onDone }) {
    const [active, setActive] = useState(mfa); // current stage/token (can switch to email fallback)
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [backup, setBackup] = useState(null);
    const [sessionData, setSessionData] = useState(null);
    const [resent, setResent] = useState(false);
    const [copied, setCopied] = useState(false);

    const isEnroll = active.stage === "totp_enroll";
    const isEmail = active.stage === "email_otp";

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        const path = isEmail ? "/auth/2fa/email/verify" : "/auth/2fa/totp/verify";
        try {
            const { data } = await api.post(path, { mfa_token: active.mfa_token, code: code.trim() });
            if (data.backup_codes?.length) { setBackup(data.backup_codes); setSessionData(data); }
            else onDone(data);
        } catch (err) { setError(fmtErr(err.response?.data?.detail) || "Verification failed"); }
        finally { setBusy(false); }
    };

    const resend = async () => {
        try { await api.post("/auth/2fa/email/resend", { mfa_token: active.mfa_token }); setResent(true); setTimeout(() => setResent(false), 4000); }
        catch (err) { setError(fmtErr(err.response?.data?.detail)); }
    };

    const useEmailFallback = async () => {
        setError(""); setBusy(true);
        try {
            const { data } = await api.post("/auth/2fa/staff-email", { mfa_token: active.mfa_token });
            setCode(""); setActive(data);
        } catch (err) { setError(fmtErr(err.response?.data?.detail) || "Could not send email code"); }
        finally { setBusy(false); }
    };

    if (backup) {
        return (
            <div className="max-w-sm w-full" data-testid="mfa-backup-codes">
                <div className="flex items-center gap-2 text-emerald-700 mb-3"><ShieldCheck className="w-5 h-5" /><span className="font-display font-bold text-lg">Two-factor is on</span></div>
                <p className="text-sm text-muted-foreground mb-4">Save these <strong>backup codes</strong> somewhere safe. Each works once if you lose your authenticator device.</p>
                <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-border rounded-sm p-3 font-num text-sm mb-3">
                    {backup.map((b) => <div key={b} data-testid={`backup-code-${b}`} className="tracking-widest">{b}</div>)}
                </div>
                <button data-testid="mfa-copy-backup" onClick={() => { navigator.clipboard?.writeText(backup.join("\n")); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className="text-xs text-brand inline-flex items-center gap-1 mb-4">{copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy codes</>}</button>
                <button data-testid="mfa-backup-continue" onClick={() => onDone(sessionData)} className="w-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm py-2.5 rounded-sm">I've saved my backup codes — continue</button>
            </div>
        );
    }

    return (
        <div className="max-w-sm w-full" data-testid="mfa-step">
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-display mb-3">Two-factor authentication</div>
            <div className="flex items-center gap-2 mb-2">
                {isEmail ? <Mail className="w-5 h-5 text-brand" /> : <Smartphone className="w-5 h-5 text-brand" />}
                <h1 className="font-display text-2xl font-bold tracking-tight">{isEnroll ? "Set up your authenticator" : isEmail ? "Enter your email code" : "Enter your code"}</h1>
            </div>

            {isEnroll && (
                <div className="mb-4">
                    <p className="text-sm text-muted-foreground mb-3">Scan this QR code with Google Authenticator, Microsoft Authenticator or Authy, then enter the 6-digit code it shows.</p>
                    <img src={active.qr} alt="Authenticator QR code" data-testid="mfa-qr" className="w-44 h-44 border border-border rounded-sm bg-white p-2" />
                    <p className="text-[11px] text-muted-foreground mt-2">Can't scan? Enter this key manually: <span className="font-num break-all" data-testid="mfa-secret">{active.secret}</span></p>
                </div>
            )}
            {isEmail && <p className="text-sm text-muted-foreground mb-4">We've sent a 6-digit code to <strong data-testid="mfa-email-hint">{active.email_hint}</strong>. It expires in 10 minutes.</p>}
            {active.stage === "totp_verify" && <p className="text-sm text-muted-foreground mb-4">Open your authenticator app and enter the current 6-digit code. You can also use a backup code.</p>}

            <form onSubmit={submit} className="space-y-4" data-testid="mfa-form">
                <div className="flex items-center border border-input rounded-sm bg-white focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
                    <KeyRound className="w-4 h-4 text-muted-foreground mx-3" />
                    <input data-testid="mfa-code-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoFocus
                        placeholder="123456" className="flex-1 py-2.5 pr-3 bg-transparent outline-none text-sm font-num tracking-widest" required />
                </div>
                {error && <div data-testid="mfa-error" className="text-xs text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2 rounded-sm">{error}</div>}
                <button data-testid="mfa-submit" disabled={busy} type="submit" className="w-full bg-brand hover:bg-brand-hover text-white font-display font-semibold text-sm py-2.5 rounded-sm disabled:opacity-50">
                    {busy ? "Verifying…" : isEnroll ? "Verify & turn on 2FA" : "Verify"}
                </button>
            </form>

            {isEmail
                ? <button data-testid="mfa-resend" onClick={resend} className="text-xs text-brand mt-3">{resent ? "New code sent ✓" : "Didn't get it? Resend code"}</button>
                : <button data-testid="mfa-email-fallback" disabled={busy} onClick={useEmailFallback} className="text-xs text-brand mt-3 inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> No authenticator app? Email me a sign-in code instead</button>}
        </div>
    );
}
